import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Shield, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '@/components/SEO';

const Home = () => {
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalCards = 3;

  const scrollToCard = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.offsetWidth;
      container.scrollTo({
        left: cardWidth * index,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.offsetWidth;
      const newIndex = Math.round(container.scrollLeft / cardWidth);
      setCurrentIndex(newIndex);
    }
  };

  const goLeft = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToCard(newIndex);
  };

  const goRight = () => {
    const newIndex = Math.min(totalCards - 1, currentIndex + 1);
    scrollToCard(newIndex);
  };

  return (
    <div>
      <SEO
        title="Unifying Identity for Secure, Compliant Growth"
        description="One unified identity platform that secures access, builds trust with your prospects, and scales with your growth — delivered in weeks, not months."
        keywords="identity management, access control, SSO, MFA, cybersecurity, SOC 2, compliance, IAM, single sign-on"
        ogTitle="IDNTY - Secure Identity Platform"
        ogDescription="One unified identity platform that secures access, builds trust with your prospects, and scales with your growth — delivered in weeks, not months."
        canonicalUrl="https://idnty.one"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'IDNTY - Secure Identity Platform',
          description: 'One unified identity platform that secures access, builds trust with your prospects, and scales with your growth.',
          url: 'https://idnty.one'
        }}
      />
      {/* Hero */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="hero-title" style={{ marginBottom: '1.5rem' }}>
            UNIFYING IDENTITY FOR SECURE, COMPLIANT GROWTH
          </h1>
          <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', lineHeight: '1.8', maxWidth: '640px', margin: '0 auto 2.5rem' }}>
            <span style={{ color: 'var(--gold)', fontWeight: '600' }}>One unified identity platform</span> that secures access, 
            builds trust with your prospects, and scales with your growth — delivered in weeks, not months.
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/contact" className="btn">
              Schedule Assessment
            </Link>
            <Link to="/pricing" className="btn btn-secondary">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="section-padding" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ marginBottom: '1.5rem', color: 'var(--white)' }}>
            YOU KNOW YOU NEED BETTER ACCESS CONTROL. BUT...
          </h2>
          <div style={{ maxWidth: '720px', fontSize: '1.1rem', lineHeight: '1.9' }}>
            <p style={{ marginBottom: '1.25rem' }}>
              Your engineering lead is still using the shared admin password from 2022.
            </p>
            <p style={{ marginBottom: '1.25rem' }}>
              Offboarding means Slacking IT: "hey can you remove Sarah from everything?"
            </p>
            <p style={{ marginBottom: '1.25rem' }}>
              Your prospect's security team is asking about SSO, MFA, and access controls before they'll even take a demo.
            </p>
            <p style={{ marginBottom: '1.25rem' }}>
              And when investors or customers ask about SOC 2 or how you manage identity and access, you're 6–9 months away from having an answer.
            </p>
            <p style={{ fontWeight: 'bold', marginTop: '2rem', paddingTop: '1rem', borderTop: '2px solid var(--gold)' }}>
              You need identity infrastructure. Not another tool to manage.
            </p>
          </div>
        </div>
      </section>

      {/* What Unified Identity Delivers */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h3 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '1.1rem' }}>
            WHAT UNIFIED IDENTITY DELIVERS
          </h3>
          {/* Horizontal Scrolling Container - 1 card per view with arrows */}
          <div style={{ position: 'relative' }}>
            {/* Left Arrow */}
            <button 
              onClick={goLeft}
              disabled={currentIndex === 0}
              style={{ 
                position: 'absolute', 
                left: '-1rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                zIndex: 10,
                background: 'var(--white)',
                border: '2px solid var(--charcoal)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentIndex === 0 ? 0.3 : 1,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} style={{ color: 'var(--charcoal)' }} />
            </button>
            
            {/* Right Arrow */}
            <button 
              onClick={goRight}
              disabled={currentIndex === totalCards - 1}
              style={{ 
                position: 'absolute', 
                right: '-1rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                zIndex: 10,
                background: 'var(--white)',
                border: '2px solid var(--charcoal)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: currentIndex === totalCards - 1 ? 'not-allowed' : 'pointer',
                opacity: currentIndex === totalCards - 1 ? 0.3 : 1,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              aria-label="Scroll right"
            >
              <ChevronRight size={24} style={{ color: 'var(--charcoal)' }} />
            </button>

            {/* Scroll Container */}
            <div 
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={{
                display: 'flex',
                overflowX: 'auto',
                gap: '2rem',
                paddingBottom: '1.5rem',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <div className="card-heavy" style={{ background: 'var(--white)', padding: '2rem', minWidth: '100%', flex: '0 0 100%', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column' }}>
                <img 
                  src="/User Lifecycle & Access.png" 
                  alt="User Lifecycle & Access" 
                  style={{ width: '100%', height: 'auto', marginBottom: '1.5rem', borderRadius: '4px' }}
                />
                <h4 style={{ color: 'var(--gold)', marginBottom: '1rem', fontSize: '1.25rem' }}>User Lifecycle & Access</h4>
                <p style={{ margin: 0, lineHeight: '1.8', fontSize: '1rem', color: 'var(--gray)' }}>
                  Automate onboarding, role changes, and offboarding. Give new hires the right apps on day one, revoke access instantly when they leave, and get full visibility into who has which apps and seats. Reclaim unused licenses and prevent overspending.
                </p>
              </div>
              <div className="card-heavy" style={{ background: 'var(--white)', padding: '2rem', minWidth: '100%', flex: '0 0 100%', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column' }}>
                <img 
                  src="/SSO & Audit.png" 
                  alt="Security & Authentication" 
                  style={{ width: '100%', height: 'auto', marginBottom: '1.5rem', borderRadius: '4px' }}
                />
                <h4 style={{ color: 'var(--gold)', marginBottom: '1rem', fontSize: '1.25rem' }}>Security & Authentication</h4>
                <p style={{ margin: 0, lineHeight: '1.8', fontSize: '1rem', color: 'var(--gray)' }}>
                  Enforce MFA across all apps from a single policy and protect accounts with phish-resistant methods (FIDO2/passkeys where supported). Reduce weak logins, credential sprawl, and password fatigue.
                </p>
              </div>
              <div className="card-heavy" style={{ background: 'var(--white)', padding: '2rem', minWidth: '100%', flex: '0 0 100%', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column' }}>
                <img 
                  src="/Security & Authentication.png" 
                  alt="SSO & Audit" 
                  style={{ width: '100%', height: 'auto', marginBottom: '1.5rem', borderRadius: '4px' }}
                />
                <h4 style={{ color: 'var(--gold)', marginBottom: '1rem', fontSize: '1.25rem' }}>SSO & Audit</h4>
                <p style={{ margin: 0, lineHeight: '1.8', fontSize: '1rem', color: 'var(--gray)' }}>
                  One login for all applications and complete access logs, automated reporting, and compliance-ready documentation for SOC 2, ISO 27001, and more. Simplify access while maintaining full visibility and compliance.
                </p>
              </div>
            </div>
          </div>
          {/* Dot indicators */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                onClick={() => scrollToCard(index)}
                style={{
                  width: currentIndex === index ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  border: 'none',
                  background: currentIndex === index ? 'var(--gold)' : 'var(--gray)',
                  opacity: currentIndex === index ? 1 : 0.4,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  padding: 0,
                }}
                aria-label={`Go to card ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            WE BUILD YOUR IDENTITY FOUNDATION. THEN KEEP IT HEALTHY.
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '3rem', fontSize: '1.1rem', maxWidth: '640px', margin: '0 auto 3rem' }}>
            We're the implementation partner that gets you from shared passwords and manual access to audit-ready IAM with continuous trust verification.
          </p>

          <div className="grid-3">
            <div className="card-heavy" style={{ background: 'var(--white)' }}>
              <Lock size={48} style={{ marginBottom: '1.25rem', color: 'var(--gold)' }} />
              <h3 className="feature-heading" style={{ marginBottom: '0.75rem' }}>IAM Implementation</h3>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                Platform selection, SSO rollout, MFA enforcement, RBAC design, automated provisioning. Built to SOC 2 standards from day one. Done in 6–8 weeks.
              </p>
            </div>
            <div className="card-heavy" style={{ background: 'var(--light-gray)' }}>
              <Shield size={48} style={{ marginBottom: '1.25rem', color: 'var(--gold)' }} />
              <h3 className="feature-heading" style={{ marginBottom: '0.75rem' }}>Role-Based Access Control</h3>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                Define access policies by role, department, or team. Ensure the right people have the right access — no more, no less. Built-in segregation of duties.
              </p>
            </div>
            <div className="card-heavy" style={{ background: 'var(--white)' }}>
              <CheckCircle size={48} style={{ marginBottom: '1.25rem', color: 'var(--gold)' }} />
              <h3 className="feature-heading" style={{ marginBottom: '0.75rem' }}>Ongoing Governance</h3>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                Monthly or quarterly access reviews. Lifecycle management. Compliance reporting. Audit support. We don't disappear after implementation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            TWO WAYS TO WORK WITH US
          </h2>
          <div className="grid-2" style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
            <div className="card-heavy" style={{ background: 'var(--white)' }}>
              <div className="label" style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Essential</div>
              <p style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>$10K implementation + $2.5K/month ongoing support</p>
              <p style={{ fontSize: '0.95rem', color: 'var(--gray)' }}>Under 75 users, new or fixing existing IAM implementation with support.</p>
            </div>
            <div className="card-heavy" style={{ background: 'var(--gold)' }}>
              <div className="label" style={{ marginBottom: '0.5rem', color: 'var(--black)' }}>Professional</div>
              <p style={{ fontSize: '1rem', marginBottom: '0.25rem', color: 'var(--black)' }}>$20K implementation + $4.5K/month ongoing support</p>
              <p style={{ fontSize: '0.95rem', color: 'var(--charcoal)' }}>75+ users or compliance-ready infrastructure.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Link to="/pricing" className="btn">
              View Full Pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--black)', color: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="section-title" style={{ color: 'var(--white)', marginBottom: '1rem' }}>
            READY TO UNIFY YOUR IDENTITY INFRASTRUCTURE?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
            Fixed pricing. No surprises. Implementation in 6–8 weeks.
          </p>
          <Link to="/contact" className="btn btn-gold">
            Schedule Assessment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
